import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTRadio, { KTRadioType } from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

DialogSignReconciliationView.propTypes = {
    item: PropTypes.object.isRequired,
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

function DialogSignReconciliationView({ item, onCancel, onDone }) {
    // MARK: ---- PROPERTIES ----
    const [loading, setLoading] = useState(false);
    const [readyToValidate, setReadyToValidate] = useState(false);
    const [reconciliationType, setReconciliationType] = useState(AgencyConstants.ContractType.DIGITAL);
    const [amount, setAmount] = useState(0);
    const [taxRate, setTaxRate] = useState(0);
    const [revenue, setRevenue] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const selectFilePathList = useRef([]);
    const [allFiles, setAllFiles] = useState([]);
    const [paperContractDocumentFile, setPaperContractDocumentFile] = useState(null);
    const [invoiceDocumentFile, setInvoiceDocumentFile] = useState(null);
    const isOrganizationAgency = useMemo(() => {
        return item?.agency?.type === 'ORGANIZATION';
    }, [item]);

    useEffect(() => {
        if (item) {
            setAmount(Math.floor(item?.amount));
            setTaxRate(item?.taxRate * 100);
            setRevenue(Math.floor(item?.netAmount));
        }
    }, [item])

    const labelMap = {
        SALE: {
            amount: 'Tổng hoa hồng',
            revenue: 'Tổng hoa hồng đại lý được nhận'
        },
        INVESTOR: {
            amount: 'Doanh thu theo phương tiện',
            revenue: 'Doanh thu NĐT được nhận'
        },
        GROUP: {
            amount: 'Doanh thu theo phương tiện',
            revenue: 'Doanh thu NĐT được nhận'
        },
        LANDLORD: {
            amount: 'Doanh thu/phí thuê theo mặt bằng',
            revenue: 'Doanh thu chủ mặt bằng được nhận'
        }
    }

    const roleLabels = labelMap[item?.agency?.primaryRole] || {};

    // MARK: ---- METHODS ----

    async function processSelectedFiles(files) {
        for (const file of files) {
            const path = await uploadAsyncFunction(file);
            selectFilePathList.current?.push(path);
        }
    }

    async function uploadAsyncFunction(file) {
        return new Promise((resolve) => {
            if (file instanceof File) {
                miscHelper.callApiUploadFile(file).then((mediaList) => {
                    if (_.isArray(mediaList) && mediaList.length > 0) {
                        const firstMedia = _.first(mediaList);
                        const relativePath = firstMedia?.relativePath;
                        if (relativePath) {
                            resolve(relativePath);
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            } else {
                resolve(file);
            }
        });
    }

    const handleSubmit = async () => {
        setReadyToValidate(true);
        if (!reconciliationType || !Utils.isValid(amount)) {
            ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        if (Utils.isValid(amount) && amount < 0) {
            ToastHelper.showError('Số tiền phải lớn hơn hoặc bàng 0');
            return;
        }

        if (reconciliationType === AgencyConstants.ReconciliationType.PAPER) {
            if (!paperContractDocumentFile || !isOrganizationAgency && !Utils.isValid(taxRate)) {
                ToastHelper.showError('Vui lòng điền đủ thông tin');
                return;
            }
            if (isOrganizationAgency && !Utils.isValid(invoiceDocumentFile)) {
                ToastHelper.showError('Vui lòng điền đủ thông tin');
                return;
            }
            if (!isOrganizationAgency && Utils.isValid(taxRate) && (taxRate < 0 || taxRate > 100)) {
                ToastHelper.showError('Thuế phải nằm trong khoảng từ 0 đến 100');
                return;
            }
        }

        if (!_.isEmpty(selectedFiles)) {
            selectFilePathList.current = [];
            await processSelectedFiles(selectedFiles);
        }
        setAllFiles([
            ..._.filter(allFiles, (item) => typeof item === 'string'),
            ...selectFilePathList.current,
        ]);
        const attachedFiles = [
            ..._.filter(allFiles, (item) => typeof item === 'string'),
            ...selectFilePathList.current,
        ];
        setLoading(true);

        if (invoiceDocumentFile) {
            const formData = new FormData();
            formData.append('reconciliationId', item?.reconciliationId);
            formData.append('files', invoiceDocumentFile);

            await AgencyHelper.API.requestUploadInvoiceDocument(formData);
        }

        if (reconciliationType === AgencyConstants.ReconciliationType.PAPER) {
            if (paperContractDocumentFile) {
                const formData = new FormData();
                formData.append('files', paperContractDocumentFile);
                formData.append('reconciliationId', item?.reconciliationId);
                formData.append('status', 'COMPLETED');
                formData.append('amount', amount);
                if (!isOrganizationAgency) {
                    formData.append('taxRate', taxRate / 100);
                }
                formData.append('netAmount', !isOrganizationAgency ? amount * (1 - taxRate / 100) : amount);

                const result = await AgencyHelper.API.requestUploadPaperReconciliation(formData);

                if (result && _.isBoolean(result)) {
                    ToastHelper.showSuccess('Ký đối soát thành công');
                    onDone?.();
                } else {
                    ToastHelper.showError(result || 'Ký đối soát thất bại');
                }
            }

        } else {
            const params = {
                reconciliationType: reconciliationType,
                document: item?.document,
                amount,
                taxRate: !isOrganizationAgency ? taxRate / 100 : 0,
            };
            const res = await AgencyHelper.API.requestUpdateReconciliation(item?.reconciliationId, params);
            if (_.isBoolean(res) && res) {
                let result = true;
                result = await AgencyHelper.API.requestAdminSignReconciliation({
                    reconciliationId: item?.reconciliationId,
                });
                if (result && _.isBoolean(result)) {
                    ToastHelper.showSuccess('Ký đối soát thành công');
                    if (onDone) {
                        onDone();
                    }
                } else {
                    ToastHelper.showError(result || 'Ký đối soát thất bại');
                }
            } else if (_.isString(res)) {
                ToastHelper.showError(res);
            }
        }
        setLoading(false);
    };

    return (
        <div>
            <div className="row m-0 p-0 px-2">
                <div className="col-12 mb-4 px-2">
                    {UIHelper.buildFormLabel('Loại tài liệu đối soát', true)}
                    <div className="radio-inline w-100 PY-2">
                        <div className="d-flex align-items-center gap-5">
                            <KTRadio
                                name="typeDigital"
                                label="Đối soát điện tử"
                                type={KTRadioType.outline}
                                additionalClassName="radio-success"
                                onChange={(value) => {
                                    setReconciliationType('DIGITAL');
                                }}
                                checked={reconciliationType === 'DIGITAL'}
                            />
                            <KTRadio
                                name="typePaper"
                                label="Đối soát giấy"
                                type={KTRadioType.outline}
                                additionalClassName="radio-success"
                                onChange={(value) => {
                                    setReconciliationType('PAPER');
                                }}
                                checked={reconciliationType === 'PAPER'}
                            />
                        </div>
                    </div>
                </div>

                {/* file dinh kem */}
                <div className='p-0'>
                    <div className="col-12 px-2">
                        <div
                            className="mb-4"
                            style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }}
                        />
                    </div>

                    <div className="col-12 mb-4 px-2">
                        <div>
                            {UIHelper.buildFormLabel(roleLabels.amount, true)}
                            <KTFormInput
                                name={"amount"}
                                value={amount}
                                onChange={(value) => {
                                    const parsedValue = typeof value === 'string'
                                        ? parseFloat(value.replace(',', '.'))
                                        : value;

                                    setAmount(parsedValue);
                                }}
                                onBlur={readyToValidate}
                                enableCheckValid
                                type={KTFormInputType.currency}
                                placeholder={`Nhập ${roleLabels.amount?.toLowerCase()}`}
                                isValid={Utils.isValid(amount) && amount >= 0}
                                isTouched={readyToValidate}
                                feedbackText={
                                    !Utils.isValid(amount)
                                        ? `Vui lòng nhập ${roleLabels.amount?.toLowerCase()}`
                                        : amount < 0
                                            ? `${roleLabels.amount} phải lớn hơn 0`
                                            : ''
                                }
                                disabled={true}
                            />
                        </div>
                    </div>

                    {!isOrganizationAgency && (
                        <div className="col-12 mb-4 px-2">
                            <div>
                                {UIHelper.buildFormLabel('Thuế TNCN', true)}
                                <KTFormInput
                                    name={"Thuế TNCN"}
                                    value={taxRate}
                                    unit='%'
                                    onChange={(value) => {
                                        setTaxRate(value);
                                    }}
                                    onBlur={readyToValidate}
                                    enableCheckValid
                                    type={KTFormInputType.currency}
                                    placeholder={'Nhập thuế TNCN'}
                                    isValid={Utils.isValid(taxRate) && taxRate >= 0 && taxRate <= 100}
                                    isTouched={readyToValidate}
                                    feedbackText={!Utils.isValid(taxRate)
                                        ? 'Vui lòng nhập thuế TNCN'
                                        : (taxRate < 0 || taxRate > 100) ? 'Thuế TNCN phải nằm trong khoảng từ 0 đến 100' : ''
                                    }
                                    disabled={true}
                                />
                            </div>
                        </div>
                    )}

                    <div className="col-12 mb-4 px-2">
                        <div>
                            {UIHelper.buildFormLabel(roleLabels.revenue, true)}
                            <KTFormInput
                                name="revenue"
                                value={revenue}
                                onChange={(value) => { }}
                                onBlur={readyToValidate}
                                enableCheckValid
                                type={KTFormInputType.currency}
                                placeholder={'Nhập tổng số tiền'}
                                disabled={true}
                            />
                        </div>
                    </div>

                    {/* file hop dong */}
                    {reconciliationType === AgencyConstants.ReconciliationType.PAPER && (
                        <div className='row px-2 mb-2'>
                            <div className="col-6">
                                <div>
                                    {UIHelper.buildFormLabel('Tải lên file đối soát scan', true)}
                                    <label className="">
                                        {UIHelper.buildNegativeButton(
                                            'Thêm tập tin',
                                            () => { },
                                            <i className="fa-regular fa-upload" />
                                        )}
                                        <input
                                            type="file"
                                            accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                                            onChange={(e) => {
                                                const files = e.target.files;
                                                if (!files || files.length === 0) return;
                                                const file = files[0];
                                                if (file.name.length > 128) {
                                                    ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                    e.target.value = null;
                                                    return;
                                                }
                                                setPaperContractDocumentFile(file);
                                            }}
                                            hidden
                                            onClick={(e) => {
                                                e.target.value = null;
                                            }}
                                        />
                                    </label>
                                    {paperContractDocumentFile && (
                                        <div>
                                            <ImageView
                                                name={paperContractDocumentFile?.lastModified ?? paperContractDocumentFile}
                                                path={
                                                    _.isString(paperContractDocumentFile)
                                                        ? paperContractDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                        : paperContractDocumentFile?.name
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* file hoa don thue */}
                            {isOrganizationAgency && (
                                <div className="col-6">
                                    <div>
                                        {UIHelper.buildFormLabel('Tải lên hóa đơn thuế', true)}
                                        <label className="">
                                            {UIHelper.buildNegativeButton(
                                                'Thêm tập tin',
                                                () => { },
                                                <i className="fa-regular fa-upload" />
                                            )}
                                            <input
                                                type="file"
                                                accept={['.pdf']}
                                                onChange={(e) => {
                                                    const files = e.target.files;
                                                    if (!files || files.length === 0) return;
                                                    const file = files[0];
                                                    if (file.name.length > 128) {
                                                        ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                        e.target.value = null;
                                                        return;
                                                    }
                                                    setInvoiceDocumentFile(file);
                                                }}
                                                hidden
                                                onClick={(e) => {
                                                    e.target.value = null;
                                                }}
                                            />
                                        </label>
                                        {invoiceDocumentFile && (
                                            <div>
                                                <ImageView
                                                    name={invoiceDocumentFile?.lastModified ?? invoiceDocumentFile}
                                                    path={
                                                        _.isString(invoiceDocumentFile)
                                                            ? invoiceDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                            : invoiceDocumentFile?.name
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* footer */}
            <div
                className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
                {UIHelper.buildGreenButton('Duyệt & ký đối soát', handleSubmit, null, true, loading)}
            </div>
        </div>
    );
}

export default DialogSignReconciliationView;
